import * as THREE from 'three';
import type { Dog } from '../dog/Dog';
import type { Landmarks } from './Landmarks';

/**
 * A Whitman-only visitor: every few minutes a student bursts out of the dorm, arms in the
 * air, screaming about the reading, zig-zags across the lawn and runs back in. She notices,
 * barks a couple of times from where she is, and goes back to whatever she was doing.
 * Only while the Whitman landmark (with its dorm) is standing past the fence.
 */
type Phase = 'none' | 'out' | 'zig' | 'back';

const _v = new THREE.Vector3();

export class Student {
  onMessage?: (text: string) => void;
  minGap = 120;
  maxGap = 300;

  readonly root = new THREE.Group();
  private arms: THREE.Mesh[] = [];
  private legs: THREE.Mesh[] = [];
  private phase: Phase = 'none';
  private t = 0;
  private nextAt = 40;
  private to = new THREE.Vector3();
  private zigs = 0;
  private lastBark = 0;
  private barks = 0;

  constructor(
    scene: THREE.Object3D,
    private dog: Dog,
    private landmarks: Landmarks,
  ) {
    const skin = new THREE.MeshStandardMaterial({ color: 0xe8b894, roughness: 0.9 });
    const shirt = new THREE.MeshStandardMaterial({ color: 0x1e3a5f, roughness: 0.9 }); // Whitman blue
    const jeans = new THREE.MeshStandardMaterial({ color: 0x3b4a6b, roughness: 1 });
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.11, 0.3, 4, 10), shirt);
    body.position.y = 0.72;
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.1, 12, 10), skin);
    head.position.y = 1.05;
    const hair = new THREE.Mesh(new THREE.SphereGeometry(0.105, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), new THREE.MeshStandardMaterial({ color: 0x4a2e1a, roughness: 1 }));
    hair.position.y = 1.07;
    this.root.add(body, head, hair);
    for (const s of [-1, 1]) {
      const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.035, 0.32, 3, 8), skin);
      arm.position.set(0.15 * s, 0.86, 0);
      arm.geometry.translate(0, 0.16, 0); // pivot at the shoulder
      arm.rotation.z = -s * 2.6; // straight up, flung out
      this.root.add(arm);
      this.arms.push(arm);
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.045, 0.36, 3, 8), jeans);
      leg.position.set(0.07 * s, 0.5, 0);
      leg.geometry.translate(0, -0.18, 0); // pivot at the hip
      this.root.add(leg);
      this.legs.push(leg);
    }
    this.root.traverse((o) => ((o as THREE.Mesh).castShadow = true));
    this.root.visible = false;
    scene.add(this.root);
  }

  /** "Go Blues!" — send one out now */
  trigger(): void {
    if (this.phase !== 'none' || !this.landmarks.hasDorm) return;
    this.launch();
  }

  private launch(): void {
    const L = this.landmarks;
    this.root.position.copy(L.dormDoor).setY(0);
    this.root.visible = true;
    this.phase = 'out';
    this.zigs = 0;
    this.barks = 0;
    this.to.copy(L.dormLawn);
    this.face(this.to);
    this.onMessage?.(`"AAAAAAH!" — a student runs out of ${L.dorm ?? 'the dorm'}`);
    this.dog.sounds.scream();
    this.dog.notice(this.root.position);
  }

  private face(p: THREE.Vector3): void {
    _v.subVectors(p, this.root.position).setY(0);
    if (_v.lengthSq() > 1e-6) this.root.rotation.y = Math.atan2(_v.x, _v.z);
  }

  private step(dt: number, speed: number): boolean {
    _v.subVectors(this.to, this.root.position).setY(0);
    const d = _v.length();
    if (d < speed * dt) {
      this.root.position.copy(this.to).setY(0);
      return true;
    }
    this.root.position.addScaledVector(_v.normalize(), speed * dt);
    return false;
  }

  update(dt: number): void {
    this.t += dt;
    if (this.phase === 'none') {
      if (this.landmarks.hasDorm && this.t > this.nextAt && document.visibilityState === 'visible') this.launch();
      return;
    }
    // flailing arms, pumping legs, a bounce
    for (let i = 0; i < 2; i++) {
      const s = i === 0 ? -1 : 1;
      this.arms[i].rotation.z = -s * (2.6 + Math.sin(this.t * 13 + i * 2) * 0.45);
      this.arms[i].rotation.x = Math.sin(this.t * 9 + i) * 0.5;
      this.legs[i].rotation.x = Math.sin(this.t * 11 + i * Math.PI) * 0.9;
    }
    this.root.position.y = Math.abs(Math.sin(this.t * 11)) * 0.06;
    const speed = 2.4;
    switch (this.phase) {
      case 'out':
        if (this.step(dt, speed)) this.nextZig();
        break;
      case 'zig':
        if (this.step(dt, speed)) {
          if (++this.zigs >= 3 + Math.floor(Math.random() * 3)) {
            this.to.copy(this.landmarks.dormDoor).setY(0);
            this.face(this.to);
            this.phase = 'back';
          } else this.nextZig();
        }
        break;
      case 'back':
        if (this.step(dt, speed)) {
          this.root.visible = false;
          this.phase = 'none';
          this.nextAt = this.t + this.minGap + Math.random() * (this.maxGap - this.minGap);
          this.onMessage?.('…and back to the books');
        }
        break;
      default:
        break;
    }
    // she barks a couple of times at the commotion, then lets it go
    if (this.barks < 3 && this.t - this.lastBark > 1.2 + Math.random()) {
      this.dog.lookAt(this.root.position);
      this.dog.barkNow(1);
      this.lastBark = this.t;
      this.barks++;
    }
  }

  private nextZig(): void {
    const L = this.landmarks;
    this.to.copy(L.dormLawn).add(_v.set((Math.random() - 0.5) * 5, 0, (Math.random() - 0.5) * 3));
    this.face(this.to);
    this.phase = 'zig';
  }
}
